/* Les listes de prix produites depuis le catalogue, et leur état d'envoi. */

import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAgents, useClients, useListesPrix, useUsines } from "@/lib/donnees";
import { autorise, useNiveau } from "@/lib/session";
import { Kpi, Kpis, Section, Segment, Tag } from "@/ui/primitives";
import { Icon } from "@/ui/Icon";
import { Table } from "@/ui/Table";
import type { Colonne } from "@/ui/Table";
import { entier } from "@/lib/format";
import type { ListePrix } from "@/types/metier";

type Filtre = "envoyee" | "brouillon";

export function Listes() {
  const navigue = useNavigate();
  const { donnees: listes, chargement, erreur } = useListesPrix();
  const { donnees: usines } = useUsines();
  const { donnees: clients } = useClients();
  const { donnees: agents } = useAgents();
  const voitUsine = autorise(useNiveau(), "agent");
  const [filtre, setFiltre] = useState<Filtre | null>(null);

  const nomUsine = useMemo(() => new Map(usines.map((u) => [u.id, u.nom] as const)), [usines]);
  const nomClient = useMemo(() => new Map(clients.map((c) => [c.id, c.nom] as const)), [clients]);
  const nomAgent = useMemo(() => new Map(agents.map((a) => [a.id, a.nom] as const)), [agents]);

  const envoyees = listes.filter((l) => l.envoyee);
  const refs = listes.reduce((t, l) => t + Number(l.refs), 0);
  const lignes = filtre ? listes.filter((l) => (filtre === "envoyee" ? l.envoyee : !l.envoyee)) : listes;

  const colonnes: (Colonne<ListePrix> | false)[] = [
    { cle: "reference", titre: "Référence", type: "ref", filtre: "texte", sous: (l) => l.titre },
    { cle: "titre", titre: "Intitulé", classe: "name", mobile: false, filtre: "texte" },
    { cle: "client_id", titre: "Client", mobile: false, filtre: "select",
      texte: (l) => (l.client_id ? (nomClient.get(l.client_id) ?? "—") : "tous clients") },
    voitUsine && { cle: "usine_id", titre: "Usine", mobile: false, filtre: "select",
      texte: (l) => (l.usine_id ? (nomUsine.get(l.usine_id) ?? "—") : "toutes usines") },
    { cle: "refs", titre: "Références", court: "Réf.", type: "num", total: true,
      nombre: (l) => Number(l.refs) },
    { cle: "agent_id", titre: "Auteur", mobile: false, filtre: "select",
      texte: (l) => (l.agent_id ? (nomAgent.get(l.agent_id) ?? "—") : "—") },
    { cle: "cree_le", titre: "Date", type: "date", mobile: false },
    {
      cle: "envoyee",
      titre: "État",
      filtre: "select",
      texte: (l) => (l.envoyee ? "Envoyée" : "Brouillon"),
      rendu: (l) =>
        l.envoyee ? <Tag ton="ok" dot>envoyée</Tag> : <Tag ton="warn" dot>brouillon</Tag>,
    },
  ];

  if (erreur) return <div className="pad"><div className="banner">{erreur}</div></div>;

  return (
    <>
      <div className="filterbar">
        <Segment
          label="État"
          valeur={filtre}
          onChange={setFiltre}
          options={[
            { cle: null, nom: "Toutes" },
            { cle: "envoyee" as Filtre, nom: "Envoyées", n: envoyees.length },
            { cle: "brouillon" as Filtre, nom: "Brouillons", n: listes.length - envoyees.length },
          ]}
        />
      </div>

      <div className="pad">
        <Kpis>
          <Kpi k="Listes" v={entier(listes.length)} n="documents produits" />
          <Kpi k="Références" v={entier(refs)} n="cumulées sur les listes" />
          <Kpi
            k="Envoyées"
            v={entier(envoyees.length)}
            n={`${listes.length - envoyees.length} en brouillon`}
          />
        </Kpis>

        <Section titre="Listes" note={chargement ? "chargement…" : `${lignes.length} documents`}>
          <Table lignes={lignes} cle={(l) => l.id} colonnes={colonnes} chercher="Référence ou intitulé" />
        </Section>

        <Section titre="Créer une liste" note="depuis le catalogue">
          <div className="card card-pad fiche-h">
            <span className="fiche-t section-n">
              Choisissez les références dans le catalogue, puis générez le document.
            </span>
            <div className="fiche-a">
              <button type="button" className="btn btn-primary" onClick={() => navigue("/catalogue")}>
                <Icon n="catalogue" cls="" />
                Ouvrir le catalogue
              </button>
            </div>
          </div>
        </Section>
      </div>
    </>
  );
}
